import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { useTranslation } from 'react-i18next';
import { useCRUD } from '../../../../api/crud';
import { USERS_URL } from '../../../../api/users';
import { User } from '../../../../types/users';
import styles from './tenantUserDetail.module.css';
import { useHasPermission } from '../../../../contexts/auth';
import { useBreadcrumbs } from '../../../../components/ui/breadcrumps/breadcrumbsContext';

export const TenantUserDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { loading, get } = useCRUD(USERS_URL);
  const { setOverride } = useBreadcrumbs();
  const [user, setUser] = useState<User | undefined>();
  const canViewUsers = useHasPermission("users.view_user");

  const fetchUser = useCallback(async () => {
    if (!id) return;

    const result = await get(id);

    if (result?.success && result?.data) {
      setUser(result.data);
      const userName = [result.data.first_name, result.data.last_name]
        .filter(Boolean)
        .join(' ') || result.data.email || '';
      setOverride(`/tenant/users/${id}`, userName);
    } else {
      navigate('/tenant/users');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, get, navigate]);

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.addEventListener('tenantUpdated', fetchUser);

    return () => {
      window.removeEventListener('tenantUpdated', fetchUser);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!canViewUsers) {
    return null;
  }

  if (loading || !user) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.section}>
        <Typography variant="h6">
          {t('User Details')}
        </Typography>
      </Box>
      <Divider />
      <Box className={styles.section}>
        <Typography variant="subtitle1">
          {t('First Name')}: {user.first_name || ''}
        </Typography>
        <Typography variant="subtitle1">
          {t('Last Name')}: {user.last_name || ''}
        </Typography>
        <Typography variant="subtitle1">
          {t('Email')}: {user.email || ''}
        </Typography>
        <Typography variant="subtitle1">
          {t('Permission Groups')}: {user.group_names || ''}
        </Typography>
      </Box>
    </Box>
  );
};
